/**
 * Member-portal timezone accessor — the member-side counterpart to the staff
 * app's useUserTimezone (which reads auth.preferences.timezone). The member
 * portal has no AuthContext, so this hook seeds from the cached/stored timezone
 * for a synchronous first render, then loads the member's saved timezone from
 * GET /api/member-portal/settings (deduped across all callers) and updates.
 * Falls back to the browser's IANA timezone.
 */
import { useEffect, useState } from 'react';
import {
  loadMemberPreferences,
  getStoredTimezone,
  browserTimezone,
} from '@/lib/memberPreferences';
import { formatDate, formatDateInTimezone, formatRelativeTime } from '@/utils/dateTime';

/** Returns the member's saved IANA timezone (falls back to the browser's). */
export function useMemberTimezone(): string {
  const [tz, setTz] = useState<string>(() => getStoredTimezone() || browserTimezone());

  useEffect(() => {
    let cancelled = false;
    loadMemberPreferences().then((prefs) => {
      if (!cancelled && prefs?.timezone) setTz(prefs.timezone);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return tz;
}

/**
 * Timezone-aware formatting helpers pre-bound to the member's timezone —
 * mirrors the staff useFormattedDate() so member pages format dates the same way.
 */
export function useMemberFormattedDate() {
  const timezone = useMemberTimezone();

  return {
    timezone,
    fmt: {
      short: (date: string | Date) => formatDate.short(date, timezone),
      medium: (date: string | Date) => formatDate.medium(date, timezone),
      long: (date: string | Date) => formatDate.long(date, timezone),
      time: (date: string | Date) => formatDate.time(date, timezone),
      dateTime: (date: string | Date) => formatDate.dateTime(date, timezone),
      relative: (date: string | Date) => formatRelativeTime(date, timezone),
      custom: (date: string | Date, formatString: string) =>
        formatDateInTimezone(date, timezone, formatString),
    },
  };
}
